import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { OPEN_ASSISTANT_EVENT } from "../../features/toolExperience/assistantSurface";
import { useAgentContext } from "../../contexts/AgentContext";
import { useModal } from "../../hooks/useModal";
import { listApprovals } from "../../api/approvals";
import { listGoals, listSchedules } from "../../api/goals";
import ChatInterface from "../../components/AgentChat/ChatInterface";
import AgentHistory from "../../components/AgentChat/AgentHistory";
import AgentContextPanel from "../../components/AgentChat/AgentContextPanel";
import GoalsTab from "../../components/AgentChat/GoalsTab";
import ApprovalsTab from "../../components/AgentChat/ApprovalsTab";
import styles from "./AgentDashboard.module.css";
export default function AgentDashboard() {
  const agent = useAgentContext();
  const { sessions, currentSessionId, selectSession, scopeKey } = agent;
  const [drawer, setDrawer] = useState<"history" | "context" | "work" | null>(
    null,
  );
  const [workTab, setWorkTab] = useState<"goals" | "approvals">("goals");
  const [params] = useSearchParams();
  const sessionFromUrl = params.get("session"),
    tabFromUrl = params.get("tab");
  const close = () => setDrawer(null);
  const { modalRef } = useModal<HTMLElement>(!!drawer, { onClose: close });
  const autoScope = useRef<string | undefined>();
  useEffect(() => {
    if (sessionFromUrl) {
      void selectSession(sessionFromUrl);
      return;
    }
  }, [sessionFromUrl, selectSession, scopeKey]);
  useEffect(() => {
    if (
      autoScope.current === scopeKey ||
      sessionFromUrl ||
      currentSessionId ||
      !sessions.length
    )
      return;
    autoScope.current = scopeKey;
    void selectSession(sessions[0].session_id);
  }, [scopeKey, sessions, currentSessionId, selectSession, sessionFromUrl]);
  useEffect(() => {
    if (tabFromUrl === "memory") setDrawer("context");
    else if (tabFromUrl === "goals" || tabFromUrl === "approvals") {
      setWorkTab(tabFromUrl);
      setDrawer("work");
    } else if (tabFromUrl === "chat") setDrawer(null);
  }, [tabFromUrl]);
  useEffect(() => {
    const open = () => {
      setDrawer(null);
      requestAnimationFrame(() =>
        document.querySelector<HTMLTextAreaElement>("main textarea")?.focus(),
      );
    };
    window.addEventListener(OPEN_ASSISTANT_EVENT, open);
    return () => window.removeEventListener(OPEN_ASSISTANT_EVENT, open);
  }, []);
  useEffect(() => {
    agent.setAssistantVisible(true);
    return () => agent.setAssistantVisible(false);
  }, [agent.setAssistantVisible]);
  const { data: approvals } = useQuery({
    queryKey: ["approvals"],
    queryFn: () => listApprovals(),
    refetchInterval: 30_000,
  });
  const pendingApprovals = approvals?.length ?? 0;

  // Badge the Goals tab with upcoming background work (e.g. the one-shot
  // follow-up check the agent books after starting an async job), so the user
  // notices something new appeared without opening the tab. Shares the query
  // cache with GoalsTab — no duplicate fetches.
  const { data: goals } = useQuery({
    queryKey: ["goals"],
    queryFn: () => listGoals(),
    staleTime: 60_000,
  });
  const { data: schedules } = useQuery({
    queryKey: ["schedules"],
    queryFn: () => listSchedules(),
    refetchInterval: 30_000,
  });
  const goalTaskIds = new Set(
    (goals ?? []).map((g) => g.scheduled_task_id).filter(Boolean),
  );
  const upcomingTasks = (schedules ?? []).filter(
    (t) => t.enabled && !goalTaskIds.has(t.id),
  ).length;

  const title =
    sessions.find((s) => s.session_id === currentSessionId)?.title ||
    "New conversation";
  const request =
    [...agent.messages].reverse().find((m) => m.type === "user")?.content || "";
  return (
    <div className={styles.dashboard}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <span className={styles.eyebrow}>AI ASSISTANT</span>
          <h1 title={title}>{title}</h1>
        </div>
        <nav aria-label="Conversation controls">
          <button onClick={() => void agent.newSession()}>New chat</button>
          <button onClick={() => setDrawer("history")}>History</button>
          <button onClick={() => setDrawer("context")}>Context</button>
          <button onClick={() => setDrawer("work")}>
            Work{pendingApprovals > 0 ? ` (${pendingApprovals})` : ""}
            {upcomingTasks > 0 && (
              <span aria-label={`${upcomingTasks} upcoming tasks`}>
                {" "}
                · {upcomingTasks}
              </span>
            )}
          </button>
        </nav>
      </header>
      <section className={styles.chatArea} aria-label="Conversation">
        <ChatInterface
          conversationKey={`${scopeKey}:${currentSessionId}`}
          messages={agent.messages}
          isThinking={agent.isThinking}
          error={agent.error}
          pendingPlan={agent.pendingPlan}
          onSend={agent.sendMessage}
          onApprove={agent.approvePlan}
          onReject={agent.rejectPlan}
          draft={{ value: agent.draftMessage, onChange: agent.setDraftMessage }}
          unsentRequest={agent.unsentRequest}
          onClearUnsentRequest={agent.clearUnsentRequest}
          activeRun={agent.activeRun}
          isConnected={agent.isConnected}
          canStop={agent.canStop}
          onStop={agent.stopRun}
          focused
          planActionsDisabled={!agent.isConnected}
        />
      </section>
      {drawer && (
        <div
          className={styles.backdrop}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <aside
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-label={
              drawer === "history"
                ? "History"
                : drawer === "context"
                  ? "Context"
                  : "Work"
            }
            tabIndex={-1}
            className={styles.drawer}
          >
            <div className={styles.drawerHeader}>
              <h2>
                {drawer === "history"
                  ? "History"
                  : drawer === "context"
                    ? "Context"
                    : "Work"}
              </h2>
              <button onClick={close} aria-label={`Close ${drawer}`}>
                Close ×
              </button>
            </div>
            <div className={styles.drawerBody}>
              {drawer === "history" ? (
                <AgentHistory
                  sessions={sessions}
                  current={currentSessionId}
                  onSelect={(id) => {
                    void selectSession(id);
                    close();
                  }}
                  onRename={agent.renameSession}
                  onDelete={agent.removeSession}
                />
              ) : drawer === "context" ? (
                <AgentContextPanel
                  scopeKey={scopeKey}
                  request={request}
                  onClose={close}
                />
              ) : (
                <>
                  <nav className={styles.workTabs} aria-label="Work sections">
                    <button
                      aria-pressed={workTab === "goals"}
                      onClick={() => setWorkTab("goals")}
                    >
                      Goals{upcomingTasks > 0 ? ` (${upcomingTasks})` : ""}
                    </button>
                    <button
                      aria-pressed={workTab === "approvals"}
                      onClick={() => setWorkTab("approvals")}
                    >
                      Approvals
                      {pendingApprovals > 0 ? ` (${pendingApprovals})` : ""}
                    </button>
                  </nav>
                  {workTab === "goals" ? <GoalsTab /> : <ApprovalsTab />}
                </>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
